import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Box,
    Stack,
    TextField, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button
} from '@mui/material';
import {useAppSelector, useAppDispatch} from '../../app/hooks';
import {
    actionsCell,
    editButton, editIcon,
    createButton, createIcon,
    searchField,
    searchInner,
    tableCell,
    tableContainer,
    tableHeaderCell
} from "./advertisersStyles";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import TablesPagination from "../../components/TablesPagination/TablesPagination";
import {createGetUsersQuery} from "../../helpers/createGetUsersQuery";
import {getAdvertisers, IAdvertiser, setItemsPerPage, setPageNumber} from "./advertisersSlice";
import {useSortingAndSearch} from './hooks/useSortingAndSearch';
import AdvertiserForm from './components/AdvertiserForm';


interface ITableHeaders {
    id: keyof IAdvertiser;
    label: string
}

const tableHeaders: ITableHeaders[] = [
    {id: 'name', label: 'Name'},
    {id: 'socialMedia', label: 'Social Media'},
    {id: 'contact', label: 'Contact'}
];

const AdvertisersPage: React.FC = () => {
    const [isOpenForm, setIsOpenForm] = useState<boolean>(false);
    const [editingAdvertiser, setEditingAdvertiser] = useState<IAdvertiser | null>(null);
    const {sorting, searchValue, setSearchValue, handleSorting} = useSortingAndSearch();
    const dispatch = useAppDispatch();
    const {advertisers, totalCount, pageNumber, itemsPerPage} = useAppSelector((state) => state.advertisers);

    useEffect(() => {
        const queryParams = createGetUsersQuery(
            pageNumber,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getAdvertisers(queryParams));
    }, []);


    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        dispatch(setPageNumber(newPage))

        const queryParams = createGetUsersQuery(
            newPage + 1,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getAdvertisers(queryParams));
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setPageNumber(0));
        dispatch(setItemsPerPage(+event.target.value))

        const queryParams = createGetUsersQuery(
            1,
            +event.target.value,
            sorting,
            searchValue
        )
        dispatch(getAdvertisers(queryParams));
    }

    const handleEditAdvertiser = (currentAdvertiser: IAdvertiser) => {
        setEditingAdvertiser(currentAdvertiser);
        setIsOpenForm(true);
    }

    const handleCreateAdvertiser = () => {
        setIsOpenForm(true);
    }

    const onCloseForm = () => {
        setEditingAdvertiser(null);
        setIsOpenForm(false);
    }

    return (
        <Container sx={{mt: 4}}>
            {isOpenForm &&
                <AdvertiserForm
                    editingAdvertiser={editingAdvertiser}
                    onCloseForm={onCloseForm}
                />
            }

            <Typography variant="h4" gutterBottom>
                Advertisers
            </Typography>
            <Box sx={{mt: 4}}>
                <Box sx={searchInner}>
                    <Stack spacing={2} sx={{width: 300}}>
                        <TextField
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            label="Search"
                            sx={searchField}
                        />
                    </Stack>
                    <Button  onClick={() => setIsOpenForm(true)}>
                        Create
                    </Button>
                </Box>
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={tableContainer}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map(header => (
                                        <TableCell
                                            sx={(theme) => ({
                                                backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                                ...tableHeaderCell,
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark' ? '#90caf9' : '#9cbed9',
                                                },
                                            })}
                                            key={header.id}
                                            onClick={() => handleSorting(header.id)}
                                        >
                                            {header.label}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={(theme) => ({
                                            backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                            ...actionsCell
                                        })}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {advertisers.map(advertiser => {
                                    return (
                                        <TableRow key={advertiser.id}>
                                            <TableCell sx={{...tableCell}}>{advertiser.name}</TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    advertiser.socialMedia &&
                                                    <Box>
                                                        {advertiser.socialMedia.facebook &&
                                                            <p>facebook: {advertiser.socialMedia.facebook}</p>
                                                        }
                                                        {advertiser.socialMedia.linkedIn &&
                                                            <p>linkedIn: {advertiser.socialMedia.linkedIn}</p>
                                                        }
                                                        {advertiser.socialMedia.comments &&
                                                            <p>comments: {advertiser.socialMedia.comments}</p>
                                                        }
                                                    </Box>
                                                }
                                            </TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    advertiser.contact &&
                                                    <Box>
                                                        <p>
                                                            {advertiser.contact.country}
                                                            {advertiser.contact.hometown && `,${advertiser.contact.hometown}`}
                                                        </p>
                                                        <p>{advertiser.contact.telephon}</p>
                                                        <p>{advertiser.contact.email}</p>
                                                    </Box>
                                                }
                                            </TableCell>

                                            <TableCell sx={{...tableCell, textAlign: "center"}}
                                            >
                                                <Button
                                                    sx={editButton}
                                                    onClick={() => handleEditAdvertiser(advertiser)}
                                                >
                                                    <BorderColorIcon sx={editIcon}/>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablesPagination
                        totalCount={totalCount}
                        itemsPerPage={itemsPerPage}
                        pageNumber={pageNumber}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default AdvertisersPage;
